import { type QueryObserverResult } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'

import { Banner } from 'slab'
import { useGetPartnersEmbeddedOnboardingSessionStatus, useGetQuotingProspectsProspectIdCensus } from 'utils/web_api'
import { type QuotingEmployeeCensus, type QuotingPaginatedEmployeeCensusList } from 'utils/web_api.schemas'

import { useEmployer } from './EmployerProvider'
import { useEmbeddedOnboarding } from '../../components/hooks/employer_onboarding/useEmbeddedOnboarding'

type CensusContextType = {
  census: QuotingEmployeeCensus | null;
  refetch: ReturnType<typeof useGetQuotingProspectsProspectIdCensus>['refetch'];
}

const CensusContext = createContext<CensusContextType>({
  census: null,
  refetch: async () => ({ data: null, isLoading: false, isError: false, error: null } as QueryObserverResult<unknown, unknown>),
})

export function useCensus () {
  return useContext(CensusContext)
}

function mostRecentCensus (censuses: QuotingPaginatedEmployeeCensusList | undefined) {
  if (!censuses?.data || censuses.data.length === 0) {
    return null
  }

  return (
    [...censuses.data].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0] || null
  )
}

export function CensusProvider () {
  const { employer } = useEmployer()
  const { isEmbedded } = useEmbeddedOnboarding()

  const prospectId = employer?.quoting_prospect_id

  // For embedded onboarding, poll session status until finalized
  const {
    data: sessionStatus,
    isLoading: _isSessionLoading,
  } = useGetPartnersEmbeddedOnboardingSessionStatus({
    query: {
      enabled: isEmbedded,
      refetchInterval: (query) => {
        const finalized = query.state.data?.finalized_at
        return finalized ? false : 1000
      },
    },
  })

  const isSessionFinalized = !isEmbedded || !!sessionStatus?.finalized_at

  const {
    data: censuses,
    isLoading,
    error,
    refetch,
  } = useGetQuotingProspectsProspectIdCensus(prospectId ?? '', undefined, {
    query: {
      refetchOnMount: 'always',
      enabled: !!prospectId && isSessionFinalized,
      refetchInterval: (query) => {
        const census = mostRecentCensus(query.state.data)
        if (['processed', 'failed'].includes(census?.status ?? '')) {
          return false
        }

        return 1000
      },
    },
  })

  // TODO: Handle pagination. Currently assumes all censuses are returned in
  // the first page.
  const currentCensus = useMemo(
    () => mostRecentCensus(censuses),
    [censuses?.data]
  )

  const value = useMemo(
    () => ({ census: currentCensus, refetch }),
    [currentCensus, refetch]
  )

  // Show loading state while waiting for embedded session to finalize
  if (isEmbedded && !isSessionFinalized) {
    return (
      <div className='flex grow items-center justify-center'>
        <div className='text-center'>
          <LoaderCircleIcon size={32} className='animate-spin text-gray-500 mx-auto mb-4' />
          <p className='text-gray-700 font-medium'>Importing employee data...</p>
          <p className='text-gray-500 text-sm mt-2'>This may take a moment for larger companies</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mt-20 w-full max-w-[45rem] mx-auto'>
        <Banner type='critical' title='Error loading census'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <CensusContext.Provider value={value}>
      <Outlet />
    </CensusContext.Provider>
  )
}
